import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { STATUS_META, fmtDate, isOverdue } from "@/lib/constants";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const MS = 86400000;
const MIN_DAY = 3;
const MAX_DAY = 48;
const parse = (s) => (s ? new Date(s + "T00:00:00") : null);
const addDays = (d, n) => new Date(d.getTime() + n * MS);
const diffDays = (a, b) => Math.round((b - a) / MS);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LABEL_W = 220;

export const TimelineView = ({ tasks, onOpenTask }) => {
  const [dayW, setDayW] = useState(24);
  const wrapRef = useRef(null);

  const scheduled = useMemo(
    () => tasks
      .map((t) => {
        const s = parse(t.start_date) || parse(t.end_date);
        const e = parse(t.end_date) || parse(t.start_date);
        return s && e ? { ...t, _s: s, _e: e < s ? s : e } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._s - b._s),
    [tasks]
  );

  const range = useMemo(() => {
    if (!scheduled.length) return null;
    let min = scheduled[0]._s, max = scheduled[0]._e;
    scheduled.forEach((t) => { if (t._s < min) min = t._s; if (t._e > max) max = t._e; });
    min = addDays(min, -2);
    max = addDays(max, 3);
    return { min, max, days: diffDays(min, max) + 1 };
  }, [scheduled]);

  const fit = useCallback(() => {
    if (!range || !wrapRef.current) return;
    const avail = wrapRef.current.clientWidth - LABEL_W - 4;
    const w = Math.floor(avail / range.days);
    setDayW(Math.max(MIN_DAY, Math.min(MAX_DAY, w)));
  }, [range]);

  // Fit once on first render with a known range.
  useLayoutEffect(() => { fit(); }, [range?.days, fit]);

  const unscheduled = tasks.length - scheduled.length;

  if (!range) {
    return <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="timeline-view"><p className="text-zinc-500 text-sm">Add start and end dates to tasks to see them on the timeline.</p></div>;
  }

  const width = range.days * dayW;
  const months = [];
  let d = new Date(range.min);
  while (d <= range.max) {
    const left = diffDays(range.min, d) * dayW;
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, left });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  const step = (dir) => setDayW((w) => Math.max(MIN_DAY, Math.min(MAX_DAY, w + dir * 6)));

  return (
    <div data-testid="timeline-view" ref={wrapRef}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs text-zinc-400">{unscheduled > 0 ? `${unscheduled} task${unscheduled !== 1 ? "s" : ""} without dates hidden` : "\u00A0"}</p>
        <div className="flex items-center gap-1">
          <button onClick={() => step(-1)} disabled={dayW <= MIN_DAY} className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40" title="Zoom out" data-testid="timeline-zoom-out"><ZoomOut className="h-4 w-4" /></button>
          <button onClick={() => step(1)} disabled={dayW >= MAX_DAY} className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40" title="Zoom in" data-testid="timeline-zoom-in"><ZoomIn className="h-4 w-4" /></button>
          <button onClick={fit} className="h-8 px-3 flex items-center gap-1.5 rounded-sm border border-zinc-200 hover:bg-zinc-50 text-xs font-medium" title="Fit to width" data-testid="timeline-fit"><Maximize2 className="h-3.5 w-3.5" /> Fit</button>
        </div>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <div style={{ width: LABEL_W + width }}>
          <div className="flex sticky top-0 z-10 bg-white border-b border-zinc-200">
            <div className="flex-shrink-0 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 border-r border-zinc-200" style={{ width: LABEL_W }}>Task</div>
            <div className="relative" style={{ width }}>
              {months.map((m) => (
                <span key={m.key} className="absolute top-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 whitespace-nowrap" style={{ left: m.left + 4 }}>{m.label}</span>
              ))}
              <div className="h-8" />
            </div>
          </div>
          {scheduled.map((t) => {
            const left = diffDays(range.min, t._s) * dayW;
            const w = Math.max(dayW, (diffDays(t._s, t._e) + 1) * dayW);
            const c = STATUS_META[t.status];
            return (
              <div key={t.task_id} className="flex items-center border-b border-zinc-100 hover:bg-zinc-50" data-testid={`timeline-row-${t.task_id}`}>
                <div className="flex-shrink-0 px-3 py-2 border-r border-zinc-100" style={{ width: LABEL_W }}>
                  <p className="text-xs font-medium truncate">{t.name}</p>
                </div>
                <div className="relative py-2" style={{ width }}>
                  <button onClick={() => onOpenTask(t)} data-testid={`timeline-bar-${t.task_id}`}
                    className="absolute h-6 rounded-sm flex items-center px-2 hover:opacity-90 transition-opacity overflow-hidden"
                    style={{ left, width: w, background: c.bg, border: `1px solid ${isOverdue(t) ? "#E11D48" : c.color}` }}
                    title={`${t.name} · ${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}`}>
                    <span className="text-[10px] font-medium truncate" style={{ color: isOverdue(t) ? "#E11D48" : c.color }}>{t.name}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
